import clsx from 'clsx'
import React, { forwardRef } from 'react'
import type {
  AriaAttributes,
  KeyboardEventHandler,
  HTMLAttributeAnchorTarget,
  MouseEventHandler,
  Ref,
  ReactNode,
} from 'react'
import type SvgIcon from '../SvgIcon/SvgIcon'
import { toDataSet } from '../utilities/toDataSet'

export interface ButtonProps {
  /**
   * Identifies the element whose contents or presence are controlled by the button
   */
  'aria-controls'?: AriaAttributes['aria-controls']
  /**
   * Indicates whether the element controlled by the button is expanded or collapsed
   */
  'aria-expanded'?: AriaAttributes['aria-expanded']
  /**
   * Indicates the availability and type of interactive popup element that can be triggered by the button
   */
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  /**
   * Identifies the element (or elements) that labels the button.
   */
  'aria-labelledby'?: AriaAttributes['aria-labelledby']
  /**
   * The content to display inside the button
   */
  children?: ReactNode & { type?: { uiName?: string } }
  /**
   * The color scheme of the button
   * @default 'default'
   */
  color?: ButtonColor
  /**
   * Custom data attributes to be added to the button element
   */
  dataSet?: Record<string, string | undefined>
  /**
   * If true, the button is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Icon to display at the end of the button
   */
  endAdornment?: typeof SvgIcon
  /**
   * If true, the button stretches to the full width of its container
   * @default false
   */
  fullWidth?: boolean
  /**
   * The id of the form element that the button is associated with
   */
  form?: string
  /**
   * If provided, the button will render as an anchor tag with this URL.
   * Otherwise, the component will render as a <button> element.
   */
  href?: string
  /**
   * The id attribute of the button element
   */
  id?: string
  /**
   * Callback fired when the button is activated
   */
  onClick?: MouseEventHandler<HTMLElement>
  /**
   * Callback fired when a key is pressed while the button is focused
   */
  onKeyDown?: KeyboardEventHandler<HTMLElement>
  /**
   * Position of the button within a button group. Used to adjust border radius and borders.
   * Only applicable when the button is used within a `ButtonGroup` component
   */
  position?: ButtonPosition | null
  /**
   * The size of the button
   * @default 'medium'
   */
  size?: ButtonSize
  /**
   * Icon to display at the start of the button
   */
  startAdornment?: typeof SvgIcon
  /**
   * Tab order index for keyboard navigation
   * @default 0
   */
  tabIndex?: number
  /**
   * Specifies where to open the linked document.
   * Only applicable when `href` is provided
   */
  target?: HTMLAttributeAnchorTarget
  /**
   * The type attribute of the button element.
   * Not applicable when `href` is provided
   * @default 'button'
   */
  type?: ButtonType
  /**
   * The visual style variant of the button
   * @default 'contained'
   */
  variant?: ButtonVariant
}

export type ButtonColor =
  | 'error'
  | 'default'
  | 'inverse'
  | 'primary'
  | 'secondary'
  | 'inherit'

type ButtonColors = {
  [key in ButtonColor]: string
}

type ButtonPosition = 'left' | 'middle' | 'right'

type ButtonPositions = {
  [key in ButtonVariant]: {
    [key in ButtonColor]: { [key in ButtonPosition]: string }
  }
}

export type ButtonSize = 'large' | 'medium' | 'small'

type ButtonSizes = {
  [key in ButtonSize]: {
    root: string
    icon: string
  }
}

type ButtonType = 'button' | 'reset' | 'submit'

export type ButtonVariant = 'contained' | 'outlined' | 'text'

type ButtonVariants = {
  [key in ButtonVariant]: ButtonColors
}

type ButtonDisabledVariants = {
  [key in ButtonVariant]: {
    default: string
    inverse: string
  }
}

const sizes: ButtonSizes = {
  large: {
    root: 'min-h-12 px-4 py-3',
    icon: 'w-6 h-6',
  },
  medium: {
    root: 'min-h-10 px-2 py-2',
    icon: 'w-5 h-5',
  },
  small: {
    root: 'min-h-8 px-1 py-1',
    icon: 'w-4.5 h-4.5',
  },
}

const disabled: ButtonDisabledVariants = {
  contained: {
    default: `disabled:bg-black-15 disabled:text-black-30`,
    inverse: `disabled:bg-white-15 disabled:text-white-30`,
  },
  outlined: {
    default: `disabled:bg-transparent disabled:border-black-15 disabled:text-black-30`,
    inverse: `disabled:bg-transparent disabled:border-white-30 disabled:text-white-30`,
  },
  text: {
    default: `disabled:bg-transparent disabled:text-black-30`,
    inverse: `disabled:bg-transparent disabled:text-white-30`,
  },
}

const positions: ButtonPositions = {
  contained: {
    default: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
    inverse: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
    primary: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-primary-dark disabled:border-black-15`,
      right: 'rounded-r border-l border-primary-dark disabled:border-black-15',
    },
    secondary: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-secondary-dark disabled:border-black-15`,
      right: `rounded-r border-l border-secondary-dark disabled:border-black-15`,
    },
    error: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-error-darker disabled:border-black-15`,
      right: 'rounded-r border-l border-error-darker disabled:border-black-15',
    },
    inherit: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
  },
  outlined: {
    default: {
      left: 'rounded-l disabled:border-black-20',
      middle: 'rounded-none border-l-0 disabled:border-black-20',
      right: 'rounded-r border-l-0 disabled:border-black-20',
    },
    inverse: {
      left: 'rounded-l disabled:border-white-100',
      middle: 'rounded-none border-l-0 disabled:border-white-100',
      right: 'rounded-r border-l-0 disabled:border-white-100',
    },
    primary: {
      left: 'rounded-l disabled:border-primary-main',
      middle: 'rounded-none border-l-0 disabled:border-primary-main',
      right: 'rounded-r border-l-0 disabled:border-primary-main',
    },
    secondary: {
      left: 'rounded-l disabled:border-secondary-main',
      middle: 'rounded-none border-l-0 disabled:border-secondary-main',
      right: 'rounded-r border-l-0 disabled:border-secondary-main',
    },
    error: {
      left: 'rounded-l disabled:border-error-dark',
      middle: 'rounded-none border-l-0 disabled:border-error-dark',
      right: 'rounded-r border-l-0 disabled:border-error-dark',
    },
    inherit: {
      left: 'rounded-l disabled:border-black-20',
      middle: 'rounded-none border-l-0 disabled:border-black-20',
      right: 'rounded-r border-l-0 disabled:border-black-20',
    },
  },
  text: {
    default: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
    inverse: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
    primary: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-primary-dark disabled:border-black-15`,
      right: 'rounded-r border-l border-primary-dark disabled:border-black-15',
    },
    secondary: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-secondary-dark disabled:border-black-15`,
      right: `rounded-r border-l border-secondary-dark disabled:border-black-15`,
    },
    error: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-error-darker disabled:border-black-15`,
      right: 'rounded-r border-l border-error-darker disabled:border-black-15',
    },
    inherit: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
  },
}

const variants: ButtonVariants = {
  contained: {
    default: `bg-black-5 text-black-90 ${disabled.contained.default} active:bg-black-25 focus:bg-black-15 focus-visible:ring hover:bg-black-15`,
    inverse: `bg-white-25 text-white-100 ${disabled.contained.inverse} active:bg-white-60 focus:bg-white-45 focus-visible:ring hover:bg-white-45`,
    primary: `bg-primary-main text-white-100 ${disabled.contained.default} active:bg-primary-darker focus:bg-primary-dark focus-visible:ring hover:bg-primary-dark`,
    secondary: `bg-secondary-main text-white-100 ${disabled.contained.default} active:bg-secondary-darker focus:bg-secondary-dark focus-visible:ring hover:bg-secondary-dark`,
    error: `bg-error-main text-white-100 ${disabled.contained.default} active:bg-error-darker focus:bg-error-dark focus-visible:ring hover:bg-error-dark`,
    inherit: `bg-inherit text-inherit ${disabled.contained.default} active:bg-black-25 focus:bg-black-15 focus-visible:ring hover:bg-black-15`,
  },
  outlined: {
    default: `border border-solid border-black-20 text-black-90 ${disabled.outlined.default} active:bg-black-15 focus:bg-black-5 focus-visible:ring hover:bg-black-5`,
    inverse: `border border-solid border-white-100 text-white-100 ${disabled.outlined.inverse} active:bg-white-45 focus:bg-white-25 focus-visible:ring hover:bg-white-25`,
    primary: `border border-solid border-primary-main text-primary-main ${disabled.outlined.default} active:bg-primary-20 focus:bg-primary-10 focus-visible:ring hover:bg-primary-10`,
    secondary: `border border-solid border-secondary-main text-secondary-main ${disabled.outlined.default} active:bg-secondary-20 focus:bg-secondary-10 focus-visible:ring hover:bg-secondary-10`,
    error: `border border-solid border-error-darker text-error-darker ${disabled.outlined.default} active:bg-error-20 focus:bg-error-10 focus-visible:ring hover:bg-error-10`,
    inherit: `border border-solid border-black-20 text-inherit ${disabled.outlined.default} active:bg-black-15 focus:bg-black-5 focus-visible:ring hover:bg-black-5`,
  },
  text: {
    default: `text-black-90 ${disabled.text.default} active:bg-black-15 focus:bg-black-5 focus-visible:ring hover:bg-black-5`,
    inverse: `text-white-100 ${disabled.text.inverse} active:bg-white-45 focus:bg-white-25 focus-visible:ring hover:bg-white-25`,
    primary: `text-primary-main ${disabled.text.default} active:bg-primary-20 focus:bg-primary-10 focus-visible:ring hover:bg-primary-10`,
    secondary: `text-secondary-main ${disabled.text.default} active:bg-secondary-20 focus:bg-secondary-10 focus-visible:ring hover:bg-secondary-10`,
    error: `text-error-darker ${disabled.text.default} active:bg-error-20 focus:bg-error-10 focus-visible:ring hover:bg-error-10`,
    inherit: `text-inherit ${disabled.text.default} active:bg-black-15 focus:bg-black-5 focus-visible:ring hover:bg-black-5`,
  },
}

function ButtonComponent(
  {
    'aria-controls': controls,
    'aria-expanded': expanded,
    'aria-haspopup': hasPopup,
    'aria-labelledby': labelledBy,
    children,
    color = 'default',
    dataSet,
    disabled = false,
    endAdornment: EndAdornment,
    fullWidth = false,
    form,
    href,
    id,
    onClick,
    onKeyDown,
    position = null,
    size = 'medium',
    startAdornment: StartAdornment,
    tabIndex = 0,
    target,
    type = 'button',
    variant = 'contained',
  }: ButtonProps,
  ref: Ref<HTMLAnchorElement> & Ref<HTMLButtonElement>,
): JSX.Element {
  const Component = href ? 'a' : 'button'

  return (
    <Component
      aria-controls={controls}
      aria-expanded={expanded}
      aria-haspopup={hasPopup}
      aria-labelledby={labelledBy}
      className={clsx(
        'font-sans text-sm font-medium leading-relaxed tracking-wide disabled:cursor-not-allowed focus:outline-none',
        variants[variant][color],
        sizes[size].root,
        position ? positions[variant][color][position] : 'rounded',
        fullWidth && 'w-full',
      )}
      {...toDataSet(dataSet)}
      disabled={disabled}
      form={form}
      href={href}
      id={id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      ref={ref}
      tabIndex={tabIndex}
      {...(href ? { target } : { type })}
    >
      <div className="flex items-center justify-center">
        {StartAdornment ? (
          <span
            className={clsx(
              'flex items-center justify-center',
              sizes[size].icon,
            )}
          >
            <StartAdornment color="inherit" />
          </span>
        ) : null}
        {children?.type?.uiName === 'SvgIcon' ? (
          <span className={sizes[size].icon}>{children}</span>
        ) : (
          <span className={size === 'small' ? 'px-1' : 'px-2'}>{children}</span>
        )}
        {EndAdornment ? (
          <span
            className={clsx(
              'flex items-center justify-center',
              sizes[size].icon,
            )}
          >
            <EndAdornment color="inherit" />
          </span>
        ) : null}
      </div>
    </Component>
  )
}

const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  ButtonComponent,
)

export default Button
